import api, { API_BASE_URL } from './api';

// Interfejs dla elementu harmonogramu (z backendu)
export interface ScheduleItem {
  id: number;
  id_task: string; // Dodano id_task
  project_id: string;
  name: string;
  start_date: string;
  end_date: string;
  ssp: string;
  days: number;
  section?: number; // numer działu (opcjonalnie na froncie)
  level?: 1 | 2 | 3; // poziom w dziale (opcjonalnie na froncie)
  predecessor?: string | null;
  value?: number | null;
  resource?: string | null;
  notes?: string | null;
  expected_progress?: number;
  expected_progress_date?: string | null;
  actual_progress?: number;
  actual_progress_date?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Interfejs dla elementów harmonogramu wyświetlanych na frontendzie
export interface ScheduleItemDisplay {
  id_task: string;
  id: number;
  ssp: string;
  task: string; // Nazwa zadania na frontendzie to 'task'
  days: number;
  startDate: string; // Data w formacie string
  endDate: string; // Data w formacie string
  indent?: number; // lokalny poziom wcięcia (frontend)
  section?: number; // dział
  level?: 1 | 2 | 3; // poziom działu
  predecessor: string;
  value: number;
  resource: string;
  notes: string;
  type?: string;
  expected_progress?: number;
  expected_progress_date?: string | null;
  actual_progress?: number;
  actual_progress_date?: string | null;
}

// Interfejs dla pakietu
export interface Package {
  package_id: string;
  project_id: string;
  sequence_number: number;
  package_symbol: string;
  name: string;
  stage: string;
  package_type?: string;
  description?: string;
  documentation_link?: string;
  package_documentation_link?: string;
  parameters?: string;
  estimated_value?: number;
  created_at?: string;
  updated_at?: string;
  files?: File[];
}

// Interfejs dla zapytania
export interface Inquiry {
  inquiry_id: string;
  project_id: string;
  package_id: string;
  sequence_number: number;
  project_name: string;
  package_name: string;
  contractor_email: string;
  contractor_name?: string;
  subject: string;
  description?: string;
  status: 'Przygotowane' | 'draft gmail' | 'Wysłane';
  response_text?: string;
  response_date?: string;
  attachments?: any[];
  created_at?: string;
  updated_at?: string;
  gmail_draft_id?: string; // ID draftu w Gmail
  gmail_message_id?: string; // ID wysłanej wiadomości w Gmail
  first_name?: string; // Imię osoby, która utworzyła zapytanie
  last_name?: string; // Nazwisko osoby, która utworzyła zapytanie
  invite_to_collectifile_board?: boolean;
}

// Interfejs dla oferty
export interface Offer {
  offer_id: string;
  project_id: string;
  po_person?: string; // Przygotowuje ofertę
  client_name: string; // Zamawiający
  order_name: string; // Nazwa zamówienia
  city: string; // Miejscowość
  site_inspection?: boolean; // Wizja lokalna: Tak/nie
  site_inspection_date?: string; // TWL - Termin wizji lokalnej (YYYY-MM-DD)
  submission_deadline: string; // TSO - Termin składania oferty (YYYY-MM-DD)
  estimated_value?: number; // SWZ - Szacunkowa wartość zamówienia
  bid_bond?: number; // Wadium
  status: 'w trakcie' | 'najlepsza oferta' | 'druga oferta' | 'trzecia i dalej oferta' | 'Uzupełnianie dokumentów' | 'Wygrana' | 'odrzucona' | 'przegrana'; // Status oferty
  client_website?: string; // Strona zamawiającego
  gdrive_link?: string; // g.drive
  miro_link?: string; // miro
  client_forms_link?: string; // Dodaj formularze zamawiającego (przesyłanie plików)
  specification_link?: string; // Dodaj Specyfikację zamówienia
  inquiry_id?: string;
  package_id?: string;
  package_name?: string; // Opcjonalne, jeśli oferta nie jest bezpośrednio związana z pakietem
  contractor_email?: string;
  contractor_name?: string; // Opcjonalne, jeśli zamawiający jest głównym kontrahentem
  offer_type?: 'materiały' | 'usługi'; // Opcjonalne, jeśli rodzaj oferty nie jest zawsze wymagany
  offer_date?: string; // Opcjonalne, jeśli data oferty nie jest zawsze wymagana
  offered_price?: number;
  description?: string;
  documentation_link?: string;
  supervisor?: string;
  created_at?: string;
  updated_at?: string;
}

// Interfejs dla zamówienia
export interface Order {
  id: number;
  order_id_display: string;
  project_id: string;
  user_id?: number;
  contractor?: string;
  order_content?: string;
  delivery_date?: string; // YYYY-MM-DD
  contact_person_name?: string;
  contact_person_surname?: string;
  contact_person_phone?: string;
  status: 'Przygotowane' | 'Wysłane' | 'Draft gmail' | 'Anulowane' | 'Zrealizowane' | 'W realizacji';
  sequence_number?: number;
  createdAt: string;
  updatedAt: string;
  user?: { id: number; name: string; email: string };
  project?: { id: number; project_name: string; investment_address: string };
  gmail_draft_id?: string; // ID draftu w Gmail
  gmail_message_id?: string; // ID wysłanej wiadomości w Gmail
  first_name?: string; // Imię osoby, która utworzyła zamówienie
  last_name?: string; // Nazwisko osoby, która utworzyła zamówienie
}

// Interfejs dla danych projektu
export interface Project {
  project_id: string;
  project_name: string;
  start_date: string;
  end_date: string;
  value: number;
  budget: number;
  client_name: string;
  contract_number: string;
  contract_sign_date: string;
  contract_subject: string;
  investment_address: string;
  location_lat?: number | null;
  location_lng?: number | null;
  geocoded_at?: string | null;
  geocode_provider?: string | null;
  kzm_emails?: string;
  // Pola w DB przechowują users.user_id
  responsible_person1: string; // user_id
  responsible_person2?: string; // user_id
  // Opcjonalne rozszerzenie w odpowiedziach API
  responsiblePerson1?: { user_id: string; display_name?: string; email: string; first_name?: string; last_name?: string; avatar_url?: string };
  responsiblePerson2?: { user_id: string; display_name?: string; email: string; first_name?: string; last_name?: string; avatar_url?: string };
  warranty_period_months?: number;
  google_drive_link?: string;
  miro_link?: string;
  work_completion_deadline?: string;
  project_phase?: 'W REALIZACJI' | 'ZAKOŃCZONE W OKRESIE GWARANCJI' | 'ZAKOŃCZONE PO GWARANCJI';
  sales?: number;
  costs?: number;
  PDZ?: number;
  contract_draft_1?: string;
  contract_draft_2?: string;
  kzm_draft?: string;
  pokr_draft?: string;
  pocr_draft?: string;
  /** ID boardu w Collectifile – gdy ustawione, projekt ma board (link pod ikoną) */
  collectifile_board_id?: string | null;
}

// Interfejs dla folderu projektu
export interface ProjectFolder {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  webViewLink: string;
}

// Interfejs dla następnego ID projektu
export interface NextProjectId {
  highestFirstPart: number;
  nextFirstPart: number;
  nextSecondPart: number;
  nextIdFormatted: string;
}

// Interfejs dla KZM (Karta Zatwierdzenia Materiałów)
export interface Kzm {
  id: number;
  kzm_id?: string;
  project_id: string;
  package_id?: string | null;
  material_trade_name: string;
  material_branch: string;
  proposed_material_status: 'Zgodny z projektem' | 'Zamienny' | 'Nowy';
  applicant_company_name: string;
  applicant_person_name: string;
  submission_date: string;
  notification_emails?: string | null;
  document_applicant_details?: string | null;
  document_applicant_person?: string | null;
  status: 'Przygotowany' | 'Gotowy do wysłania' | 'Wysłano do akceptacji' | 'Wymaga korekty' | 'Zatwierdzono' | 'Zatwierdzono z uwagami' | 'Odrzucono' | 'Anulowano';
  created_by_user_id: string;
  prepared_by_user_name?: string | null;
  supervisor_decision?: 'Akceptacja bez uwag' | 'Akceptacja z uwagami' | 'Brak akceptacji' | null;
  supervisor_remarks?: string | null;
  supervisor_decision_date?: string | null;
  supervisor_signature_placeholder?: string | null;
  client_decision?: 'Akceptacja bez uwag' | 'Akceptacja z uwagami' | 'Brak akceptacji' | null;
  client_remarks?: string | null;
  client_decision_date?: string | null;
  client_signature_placeholder?: string | null;
  google_doc_template_url?: string | null;
  generated_kzm_google_doc_id?: string | null;
  generated_kzm_pdf_url?: string | null;
  drive_folder_id?: string | null;
  sequence_number?: number | null;
  created_at?: string;
  updated_at?: string;
  attachments?: KzmAttachment[];
  project?: Project;
  package?: Package;
  createdBy?: { user_id: string; display_name?: string; email: string };
}

// Interfejs dla załącznika KZM
export interface KzmAttachment {
  id: number;
  kzm_id: number;
  kzm_attachment_id?: string;
  file_name: string;
  file_description?: string | null;
  google_drive_file_id: string;
  google_drive_file_url: string;
  mime_type: string;
  file_size?: number | null;
  sequence_number?: number | null;
  uploaded_at?: string;
}

// Interfejs dla draftu umowy
export interface ContractDraft {
  id: number;
  project_id: string;
  contract_number: string;
  subcontractor_type: 'company' | 'natural_person';
  subcontractor_name: string;
  subcontractor_address?: string;
  subcontractor_city?: string;
  subcontractor_postal_code?: string;
  subcontractor_registration_court?: string;
  subcontractor_nip?: string;
  subcontractor_regon?: string;
  subcontractor_krs?: string;
  representative_name?: string;
  representative_position?: string;
  order_name: string;
  subcontractor_offer_attachment_url?: string;
  subcontractor_offer_date?: string;
  project_documentation_attachment_url?: string;
  net_renumeration: number;
  contract_completion_date?: string;
  subcontractor_email?: string;
  subcontractor_krs_ceidg_attachment_url?: string;
  status: 'draft' | 'pending_acceptance' | 'accepted' | 'signed' | 'rejected' | 'cancelled';
  google_doc_template_url?: string;
  generated_contract_google_doc_id?: string;
  generated_contract_pdf_url?: string;
  drive_folder_id?: string;
  created_by_user_id?: string;
  createdAt?: string;
  updatedAt?: string;
  project?: Project;
  success?: boolean;
  message?: string;
}

// Interfejs dla odpowiedzi API
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  count?: number;
  error?: string;
  warning?: string;
}

// Interfejs dla odpowiedzi z wysyłania wiadomości
interface EmailResponse {
  success: boolean;
  message: string;
  data?: {
    messageId?: string;
    draftId?: string;
    draftUrl?: string;
  };
  error?: string;
  requireReauth?: boolean;
  notFound?: boolean;
}

// Serwis do obsługi projektów
export const getAllProjects = async (): Promise<Project[]> => {
  try {
    const response = await api.get<ApiResponse<Project[]>>('/projects');
    if (!response.data.success && Array.isArray(response.data as any)) {
      // Fallback na surową tablicę (np. gdy serwer zwróci plain rows)
      return (response.data as unknown as Project[]) || [];
    }
    return (response.data.data as Project[]) || [];
  } catch (error) {
    console.error('Błąd podczas pobierania projektów:', error);
    throw error;
  }
};

// Minimalna lista wszystkich ofert (do zarządzania widocznością)
export const getAllOffersMinimal = async (): Promise<Offer[]> => {
  try {
    const response = await api.get<ApiResponse<Offer[]>>('/offers');
    return response.data.data || [];
  } catch (error) {
    console.error('Błąd podczas pobierania ofert (minimal):', error);
    return [];
  }
};

export const importProjectsFromDrive = async (): Promise<Project[]> => {
  try {
    const response = await api.post<ApiResponse<Project[]>>('/projects/import-from-drive');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się zaimportować projektów');
    }
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas importowania projektów z Google Drive:', error);
    throw error;
  }
};

export const getProjectById = async (projectId: string): Promise<Project | null> => {
  try {
    const response = await api.get<ApiResponse<Project>>(`/projects/${projectId}`);
    return response.data.data || null;
  } catch (error) {
    console.error(`Błąd podczas pobierania projektu o ID ${projectId}:`, error);
    throw error;
  }
};

// URL zdjęcia projektu (jeśli istnieje na serwerze)
export const getProjectPhotoUrl = async (projectId: string): Promise<string | null> => {
  try {
    const res = await api.get<any>(`/projects/${projectId}/photo-url`, { headers: { 'Cache-Control': 'no-cache' }, params: { _ts: Date.now() } });
    const payload = res.data || {};
    // Akceptuj różne kształty odpowiedzi backendu
    const rawCandidate: unknown =
      payload.url ??
      payload.data?.url ??
      (typeof payload.data === 'string' ? payload.data : undefined) ??
      payload.path ??
      null;
    if (payload.success !== false && typeof rawCandidate === 'string' && rawCandidate) {
      const rawBase = String(rawCandidate);
      // Dodaj cache-buster do obrazka, aby po uploadzie nie korzystać z przestarzałego cache przeglądarki
      const raw = rawBase.includes('?') ? `${rawBase}&_ts=${Date.now()}` : `${rawBase}?_ts=${Date.now()}`;
      // Znormalizuj ścieżki względne zwracane przez backend
      // Preferuj serwowanie jak w Board: bezpośrednio spod hosta (bez /api),
      // ponieważ reverse proxy może nie przepuszczać /api/uploads w niektórych konfiguracjach.
      if (/^https?:\/\//i.test(raw)) return raw;
      const SERVER_BASE_URL = (API_BASE_URL || '').replace(/\/?api\/?$/i, '').replace(/\/?$/, '');
      if (raw.startsWith('/')) return SERVER_BASE_URL + raw;
      return `${SERVER_BASE_URL}/${raw.replace(/^\/+/, '')}`;
    }
    return null;
  } catch {
    return null;
  }
};

// Upload zdjęcia projektu
export const uploadProjectPhoto = async (projectId: string, file: File): Promise<string> => {
  const form = new FormData();
  form.append('image', file);
  const res = await api.post<{ success: boolean; url?: string; message?: string }>(`/projects/${projectId}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  if (!res.data?.success || !res.data.url) throw new Error(res.data?.message || 'Upload zdjęcia nie powiódł się');
  return res.data.url;
};

export const createProject = async (projectData: Project): Promise<Project> => {
  try {
    const response = await api.post<ApiResponse<Project>>('/projects', projectData);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się utworzyć projektu');
    }
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas tworzenia projektu:', error);
    throw error;
  }
};

export const updateProject = async (projectId: string, projectData: Partial<Project>): Promise<Project> => {
  try {
    const response = await api.put<ApiResponse<Project>>(`/projects/${projectId}`, projectData);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się zaktualizować projektu');
    }
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji projektu o ID ${projectId}:`, error);
    throw error;
  }
};

export const deleteProject = async (projectId: string): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/projects/${projectId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania projektu o ID ${projectId}:`, error);
    throw error;
  }
};

export const getProjectFolders = async (): Promise<ProjectFolder[]> => {
  try {
    const response = await api.get<ApiResponse<ProjectFolder[]>>('/project-folders');
    return response.data.data || [];
  } catch (error) {
    console.error('Błąd podczas pobierania folderów projektów:', error);
    throw error;
  }
};

export const getNextProjectId = async (): Promise<NextProjectId> => {
  try {
    const response = await api.get<ApiResponse<NextProjectId>>('/project-folders/next-id');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się pobrać następnego ID projektu');
    }
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas pobierania następnego ID projektu:', error);
    throw error;
  }
};

export const createProjectFolder = async (projectName: string): Promise<{ folder: ProjectFolder, project?: Project }> => {
  try {
    const response = await api.post<ApiResponse<{ folder: ProjectFolder, project?: Project }>>('/project-folders', { projectName });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się utworzyć folderu projektu');
    }
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas tworzenia folderu projektu:', error);
    throw error;
  }
};

export const updateProjectPhase = async (
  projectId: string, 
  newPhase: 'W REALIZACJI' | 'ZAKOŃCZONE W OKRESIE GWARANCJI' | 'ZAKOŃCZONE PO GWARANCJI',
  folderId: string,
  sharedDriveId: string
): Promise<ApiResponse<Project>> => {
  try {
    const response = await api.patch<ApiResponse<Project>>(
      `/projects/${projectId}/phase`, 
      { newPhase, folderId, sharedDriveId }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się zaktualizować fazy projektu');
    }
    return response.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji fazy projektu o ID ${projectId}:`, error);
    throw error;
  }
};

export const getProjectFinancialData = async (projectId: string): Promise<Project> => {
  try {
    const response = await api.get<ApiResponse<{
      project: Project,
      financialData: { sprzedaz: number, koszty: number }
    }>>(`/mssql/projects/${projectId}/financial-data`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się pobrać danych finansowych projektu');
    }
    return response.data.data.project;
  } catch (error) {
    console.error(`Błąd podczas pobierania danych finansowych projektu o ID ${projectId}:`, error);
    throw error;
  }
};

export const updateFinancialData = async (phase?: 'W REALIZACJI' | 'ZAKOŃCZONE W OKRESIE GWARANCJI' | 'ZAKOŃCZONE PO GWARANCJI'): Promise<boolean> => {
  try {
    const url = phase 
      ? `/mssql/projects/update-financial-data?phase=${encodeURIComponent(phase)}`
      : '/mssql/projects/update-financial-data';
    const response = await api.post<ApiResponse<null>>(url);
    return response.data.success || false;
  } catch (error) {
    console.error('Błąd podczas aktualizacji danych finansowych projektów:', error);
    throw error;
  }
};

export const getAllProjectsFinancialData = async (): Promise<{
  projects: Array<{ project_id: string; project_name: string; sprzedaz: number; koszty: number; pdz: number; }>,
  errors: Array<{ project_id: string; project_name: string; error: string; }>
}> => {
  try {
    const response = await api.get<ApiResponse<{
      projects: Array<{ project_id: string; project_name: string; sprzedaz: number; koszty: number; pdz: number; }>,
      errors: Array<{ project_id: string; project_name: string; error: string; }>
    }>>('/mssql/projects/all-financial-data');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się pobrać danych finansowych projektów z MySQL');
    }
    return response.data.data;
  } catch (error) {
    // Soft-fail: backend może nie mieć skonfigurowanego endpointu/połączenia – zwracamy pusty wynik, aby nie psuć UX
    console.warn('[projectService] getAllProjectsFinancialData: błąd – zwracam pusty wynik (fallback). Szczegóły:', error);
    return { projects: [], errors: [] };
  }
};

export const getPackagesByProjectId = async (projectId: string): Promise<Package[]> => {
  try {
    const response = await api.get<ApiResponse<Package[]>>(`/packages/project/${projectId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się pobrać pakietów dla projektu');
    }
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania pakietów dla projektu ${projectId}:`, error);
    throw error;
  }
};

export const getPackageById = async (packageId: string): Promise<Package | null> => {
  try {
    const response = await api.get<ApiResponse<Package>>(`/packages/${packageId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się pobrać pakietu');
    }
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania pakietu o ID ${packageId}:`, error);
    throw error;
  }
};

export const createPackage = async (packageData: Partial<Package> & { files?: File[] }): Promise<Package> => {
  try {
    if (packageData.files && packageData.files.length > 0) {
      const formData = new FormData();
      const packageDataWithoutFiles = { ...packageData };
      delete packageDataWithoutFiles.files;
      formData.append('data', JSON.stringify(packageDataWithoutFiles));
      packageData.files.forEach((file) => formData.append('files', file));
      const response = await api.post<ApiResponse<Package>>('/packages', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć pakietu');
      return response.data.data;
    } else {
      const response = await api.post<ApiResponse<Package>>('/packages', packageData);
      if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć pakietu');
      return response.data.data;
    }
  } catch (error) {
    console.error('Błąd podczas tworzenia pakietu:', error);
    throw error;
  }
};

export const updatePackage = async (packageId: string, packageData: Partial<Package> & { files?: File[] }): Promise<Package> => {
  try {
    if (packageData.files && packageData.files.length > 0) {
      const formData = new FormData();
      const packageDataWithoutFiles = { ...packageData };
      delete packageDataWithoutFiles.files;
      formData.append('data', JSON.stringify(packageDataWithoutFiles));
      packageData.files.forEach((file) => formData.append('files', file));
      const response = await api.put<ApiResponse<Package>>(`/packages/${packageId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować pakietu');
      return response.data.data;
    } else {
      const response = await api.put<ApiResponse<Package>>(`/packages/${packageId}`, packageData);
      if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować pakietu');
      return response.data.data;
    }
  } catch (error) {
    console.error(`Błąd podczas aktualizacji pakietu o ID ${packageId}:`, error);
    throw error;
  }
};

export const deletePackage = async (packageId: string): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/packages/${packageId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania pakietu o ID ${packageId}:`, error);
    throw error;
  }
};

export const getInquiriesByProjectId = async (projectId: string): Promise<Inquiry[]> => {
  try {
    const response = await api.get<ApiResponse<Inquiry[]>>(`/projects/${projectId}/inquiries`);
    if (!response.data.success || !response.data.data) {
      console.warn(`[projectService] getInquiriesByProjectId: brak danych (${response.data.message || 'no data'}) dla projektu ${projectId}. Zwracam pustą listę.`);
      return [];
    }
    return response.data.data;
  } catch (error) {
    console.warn(`Błąd ściągania zapytań dla projektu ${projectId}, fallback=[]:`, error);
    return [];
  }
};

export const getInquiriesByPackageId = async (packageId: string): Promise<Inquiry[]> => {
  try {
    const response = await api.get<ApiResponse<Inquiry[]>>(`/packages/${packageId}/inquiries`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać zapytań dla pakietu');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania zapytań dla pakietu ${packageId}:`, error);
    throw error;
  }
};

export const getInquiryById = async (inquiryId: string): Promise<Inquiry | null> => {
  try {
    const response = await api.get<ApiResponse<Inquiry>>(`/inquiries/${inquiryId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać zapytania');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania zapytania o ID ${inquiryId}:`, error);
    throw error;
  }
};

export const createInquiry = async (inquiryData: Partial<Inquiry>): Promise<Inquiry> => {
  try {
    const response = await api.post<ApiResponse<Inquiry>>('/inquiries', inquiryData);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć zapytania');
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas tworzenia zapytania:', error);
    throw error;
  }
};

export const updateInquiry = async (inquiryId: string, inquiryData: Partial<Inquiry>): Promise<Inquiry> => {
  try {
    const response = await api.put<ApiResponse<Inquiry>>(`/inquiries/${inquiryId}`, inquiryData);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować zapytania');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji zapytania o ID ${inquiryId}:`, error);
    throw error;
  }
};

export const updateInquiryStatus = async (inquiryId: string, status: 'Przygotowane' | 'draft gmail' | 'Wysłane'): Promise<Inquiry> => {
  try {
    const response = await api.patch<ApiResponse<Inquiry>>(`/inquiries/${inquiryId}/status`, { status });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować statusu zapytania');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji statusu zapytania o ID ${inquiryId}:`, error);
    throw error;
  }
};

export const deleteInquiry = async (inquiryId: string): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/inquiries/${inquiryId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania zapytania o ID ${inquiryId}:`, error);
    throw error;
  }
};

export const getInquiryDataForOffer = async (inquiryId: string): Promise<ApiResponse<{ inquiry: Inquiry, package?: Package }>> => {
  try {
    const response = await api.get<ApiResponse<{ inquiry: Inquiry, package?: Package }>>(`/offers/inquiry-data/${inquiryId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać danych zapytania dla oferty');
    return response.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania danych zapytania o ID ${inquiryId} dla oferty:`, error);
    throw error;
  }
};

export const getOffersByProjectId = async (projectId: string): Promise<Offer[]> => {
  try {
    const response = await api.get<ApiResponse<Offer[]>>(`/offers/project/${projectId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać ofert dla projektu');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania ofert dla projektu ${projectId}:`, error);
    throw error;
  }
};

export const getOffersByInquiryId = async (inquiryId: string): Promise<Offer[]> => {
  try {
    const response = await api.get<ApiResponse<Offer[]>>(`/offers/inquiry/${inquiryId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać ofert dla zapytania');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania ofert dla zapytania ${inquiryId}:`, error);
    throw error;
  }
};

export const getOfferById = async (offerId: string): Promise<Offer | null> => {
  try {
    const response = await api.get<ApiResponse<Offer>>(`/offers/${offerId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać oferty');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania oferty o ID ${offerId}:`, error);
    throw error;
  }
};

export const createOffer = async (offerData: FormData): Promise<Offer> => {
  try {
    console.log('[projectService.ts] createOffer attempting to POST to /offers');
    const response = await api.post<ApiResponse<Offer>>('/offers', offerData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć oferty');
    return response.data.data;
  } catch (error: any) { 
    console.error('Błąd podczas tworzenia oferty (projectService.ts):', error.response?.data || error.message || error);
    if (error.response) throw new Error(error.response.data?.message || `Request failed with status ${error.response.status}`);
    else if (error.request) throw new Error('No response received from server.');
    else throw new Error(error.message || 'Error creating offer.');
  }
};

export const updateOffer = async (offerId: string, offerData: FormData): Promise<Offer> => {
  try {
    const response = await api.put<ApiResponse<Offer>>(`/offers/${offerId}`, offerData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się zaktualizować oferty');
    }
    return response.data.data;
  } catch (error: any) {
    console.error(`Błąd podczas aktualizacji oferty o ID ${offerId}:`, error.response?.data || error.message || error);
    if (error.response) throw new Error(error.response.data?.message || `Request failed with status ${error.response.status}`);
    else if (error.request) throw new Error('No response received from server.');
    else throw new Error(error.message || 'Error updating offer.');
  }
};

export const deleteOffer = async (offerId: string): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/offers/${offerId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania oferty o ID ${offerId}:`, error);
    throw error;
  }
};

// Funkcje do obsługi zamówień
export const getOrdersByProjectId = async (projectId: string): Promise<Order[]> => {
  try {
    const response = await api.get<Order[]>(`/orders/project/${projectId}`);
    return response.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania zamówień dla projektu ${projectId}:`, error);
    throw error;
  }
};

export const getOrderById = async (orderId: string): Promise<Order | null> => {
  try {
    const response = await api.get<ApiResponse<Order>>(`/orders/${orderId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać zamówienia');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania zamówienia o ID ${orderId}:`, error);
    throw error;
  }
};

export const createOrder = async (orderData: Partial<Order>): Promise<Order> => {
  try {
    const response = await api.post<ApiResponse<Order>>('/orders', orderData);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć zamówienia');
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas tworzenia zamówienia:', error);
    throw error;
  }
};

export const updateOrder = async (orderId: string, orderData: Partial<Order>): Promise<Order> => {
  try {
    const response = await api.put<ApiResponse<Order>>(`/orders/${orderId}`, orderData);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować zamówienia');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji zamówienia o ID ${orderId}:`, error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId: string, status: 'Przygotowane' | 'Wysłane' | 'Draft gmail' | 'Anulowane' | 'Zrealizowane' | 'W realizacji'): Promise<Order> => {
  try {
    const response = await api.patch<ApiResponse<Order>>(`/orders/${orderId}/status`, { status });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować statusu zamówienia');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji statusu zamówienia o ID ${orderId}:`, error);
    throw error;
  }
};

export const deleteOrder = async (orderId: string): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/orders/${orderId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania zamówienia o ID ${orderId}:`, error);
    throw error;
  }
};

// Funkcje do obsługi KZM
export const getKzmsByProjectId = async (projectId: string): Promise<Kzm[]> => {
  try {
    const response = await api.get<ApiResponse<Kzm[]>>(`/kzm/project/${projectId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać KZM dla projektu');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania KZM dla projektu ${projectId}:`, error);
    throw error;
  }
};

export const getKzmById = async (kzmId: string): Promise<Kzm | null> => {
  try {
    const response = await api.get<ApiResponse<Kzm>>(`/kzm/${kzmId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać KZM');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania KZM o ID ${kzmId}:`, error);
    throw error;
  }
};

export const createKzm = async (kzmData: FormData): Promise<Kzm> => {
  try {
    const response = await api.post<ApiResponse<Kzm>>('/kzm', kzmData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć KZM');
    return response.data.data;
  } catch (error: any) {
    console.error('Błąd podczas tworzenia KZM:', error.response?.data || error.message || error);
    if (error.response) throw new Error(error.response.data?.message || `Request failed with status ${error.response.status}`);
    else if (error.request) throw new Error('No response received from server.');
    else throw new Error(error.message || 'Error creating KZM.');
  }
};

export const updateKzm = async (kzmId: string | number, kzmData: FormData): Promise<Kzm> => {
  try {
    const response = await api.put<ApiResponse<Kzm>>(`/kzm/${kzmId}`, kzmData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować KZM');
    return response.data.data;
  } catch (error: any) {
    console.error(`Błąd podczas aktualizacji KZM o ID ${kzmId}:`, error.response?.data || error.message || error);
    if (error.response) throw new Error(error.response.data?.message || `Request failed with status ${error.response.status}`);
    else if (error.request) throw new Error('No response received from server.');
    else throw new Error(error.message || 'Error updating KZM.');
  }
};

export const deleteKzm = async (kzmId: string | number): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/kzm/${kzmId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania KZM o ID ${kzmId}:`, error);
    throw error;
  }
};

// Funkcje do obsługi elementów harmonogramu
export const createScheduleItem = async (scheduleItemData: Partial<Omit<ScheduleItem, 'id' | 'end_date' | 'expected_progress' | 'expected_progress_date' | 'actual_progress_date'>>): Promise<ScheduleItem> => {
  try {
    const response = await api.post<ApiResponse<ScheduleItem>>('/schedule-items', scheduleItemData);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć elementu harmonogramu');
    return response.data.data;
  } catch (error) {
    console.error('Błąd podczas tworzenia elementu harmonogramu:', error);
    throw error;
  }
};

export const updateScheduleItem = async (scheduleItemId: number, scheduleItemData: Partial<Omit<ScheduleItem, 'id' | 'end_date' | 'expected_progress' | 'expected_progress_date' | 'actual_progress_date'>>): Promise<ScheduleItem> => {
  try {
    const response = await api.put<ApiResponse<ScheduleItem>>(`/schedule-items/${scheduleItemId}`, scheduleItemData);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować elementu harmonogramu');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas aktualizacji elementu harmonogramu o ID ${scheduleItemId}:`, error);
    throw error;
  }
};

export const deleteScheduleItem = async (scheduleItemId: number): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/schedule-items/${scheduleItemId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania elementu harmonogramu o ID ${scheduleItemId}:`, error);
    throw error;
  }
};

// Funkcje do obsługi draftu umowy
export const getContractDraftsByProjectId = async (projectId: string): Promise<ContractDraft[]> => {
  try {
    const response = await api.get<ApiResponse<ContractDraft[]>>(`/contract-drafts/project/${projectId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać draftu umowy dla projektu');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania draftu umowy dla projektu ${projectId}:`, error);
    throw error;
  }
};

export const getContractDraftById = async (contractDraftId: string): Promise<ContractDraft | null> => {
  try {
    const response = await api.get<ApiResponse<ContractDraft>>(`/contract-drafts/${contractDraftId}`);
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się pobrać draftu umowy');
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania draftu umowy o ID ${contractDraftId}:`, error);
    throw error;
  }
};

export const createContractDraft = async (contractDraftData: FormData): Promise<ContractDraft> => {
  try {
    const response = await api.post<ApiResponse<ContractDraft>>('/contract-drafts', contractDraftData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się utworzyć draftu umowy');
    return response.data.data;
  } catch (error: any) {
    console.error('Błąd podczas tworzenia draftu umowy:', error.response?.data || error.message || error);
    if (error.response) throw new Error(error.response.data?.message || `Request failed with status ${error.response.status}`);
    else if (error.request) throw new Error('No response received from server.');
    else throw new Error(error.message || 'Error creating contract draft.');
  }
};

export const updateContractDraft = async (contractDraftId: string, contractDraftData: FormData): Promise<ContractDraft> => {
  try {
    const response = await api.put<ApiResponse<ContractDraft>>(`/contract-drafts/${contractDraftId}`, contractDraftData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Nie udało się zaktualizować draftu umowy');
    return response.data.data;
  } catch (error: any) {
    console.error(`Błąd podczas aktualizacji draftu umowy o ID ${contractDraftId}:`, error.response?.data || error.message || error);
    if (error.response) throw new Error(error.response.data?.message || `Request failed with status ${error.response.status}`);
    else if (error.request) throw new Error('No response received from server.');
    else throw new Error(error.message || 'Error updating contract draft.');
  }
};

export const deleteContractDraft = async (contractDraftId: string): Promise<boolean> => {
  try {
    const response = await api.delete<ApiResponse<null>>(`/contract-drafts/${contractDraftId}`);
    return response.data.success || false;
  } catch (error) {
    console.error(`Błąd podczas usuwania draftu umowy o ID ${contractDraftId}:`, error);
    throw error;
  }
};

// Funkcja do eksportu draftu umowy do PDF
export const exportContractDraftToPdf = async (contractDraftId: number): Promise<Blob> => {
  try {
    const response = await api.get(`/contract-drafts/${contractDraftId}/export-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error(`Błąd podczas eksportu draftu umowy o ID ${contractDraftId} do PDF:`, error);
    throw error;
  }
};

// Funkcje do obsługi emaili dla zamówień
export const createOrderDraft = async (orderId: string): Promise<EmailResponse> => {
  try {
    const response = await api.post<EmailResponse>(`/orders/${orderId}/create-draft`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Nie udało się utworzyć draftu emaila dla zamówienia');
    }
    return response.data;
  } catch (error: any) {
    console.error(`Błąd podczas tworzenia draftu emaila dla zamówienia o ID ${orderId}:`, error);
    if (error.response?.data) throw error.response.data;
    throw error;
  }
};

export const sendGmailDraft = async (orderId: string, draftId: string): Promise<EmailResponse> => {
  try {
    const response = await api.post<EmailResponse>(`/orders/${orderId}/send-gmail-draft`, { draftId });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Nie udało się wysłać draftu emaila dla zamówienia');
    }
    return response.data;
  } catch (error: any) {
    console.error(`Błąd podczas wysyłania draftu emaila dla zamówienia o ID ${orderId}:`, error);
    if (error.response?.data) throw error.response.data;
    throw error;
  }
};

export const sendDirectEmail = async (orderId: string): Promise<EmailResponse> => {
  try {
    const response = await api.post<EmailResponse>(`/orders/${orderId}/send-direct-email`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Nie udało się wysłać emaila dla zamówienia');
    }
    return response.data;
  } catch (error: any) {
    console.error(`Błąd podczas wysyłania emaila dla zamówienia o ID ${orderId}:`, error);
    if (error.response?.data) throw error.response.data;
    throw error;
  }
};

// Funkcja do pobierania elementów harmonogramu dla projektu
export const getScheduleItemsByProjectId = async (projectId: string): Promise<ScheduleItem[]> => {
  try {
    // Bustowanie cache po stronie przeglądarki/proxy
    const response = await api.get<ApiResponse<ScheduleItem[]>>(`/schedule-items/project/${projectId}`, { params: { _ts: Date.now() }, headers: { 'Cache-Control': 'no-cache' } });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Nie udało się pobrać elementów harmonogramu dla projektu');
    }
    return response.data.data;
  } catch (error) {
    console.error(`Błąd podczas pobierania elementów harmonogramu dla projektu ${projectId}:`, error);
    throw error;
  }
};

// Pobieranie szablonu CSV harmonogramu
export const downloadScheduleCsvTemplate = async (projectId: string): Promise<Blob> => {
  const response = await api.get(`/schedule-items/project/${projectId}/csv-template`, { responseType: 'blob' });
  return response.data;
};

// Import harmonogramu z CSV
export const importScheduleFromCsv = async (projectId: string, file: File): Promise<{ count?: number }> => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post<ApiResponse<unknown>>(`/schedule-items/project/${projectId}/import-csv`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  if (!response.data.success) throw new Error(response.data.message || 'Nie udało się zaimportować harmonogramu');
  return { count: (response.data as any).count };
};

// Funkcje pomocnicze do obsługi inicjałów i pełnych imion osób odpowiedzialnych
export const getInitials = (fullName: string): string => {
  if (!fullName) return '';
  return fullName
    .split(' ')
    .map(name => name.charAt(0).toUpperCase())
    .join('');
};

export const formatResponsiblePerson = (person: string | undefined): { initials: string; fullName: string } => {
  if (!person) return { initials: '', fullName: '' };
  const fullName = person.trim();
  const initials = getInitials(fullName);
  return { initials, fullName };
};

// Eksport domyślny wszystkich funkcji
export default {
  createContractDraft,
  createInquiry,
  createKzm,
  createOffer,
  createOrder,
  createOrderDraft,
  createPackage,
  createProject,
  createProjectFolder,
  createScheduleItem,
  deleteContractDraft,
  deleteInquiry,
  deleteKzm,
  deleteOffer,
  deleteOrder,
  deletePackage,
  deleteProject,
  deleteScheduleItem,
  exportContractDraftToPdf,
  formatResponsiblePerson,
  getAllProjects,
  getAllOffersMinimal,
  getAllProjectsFinancialData,
  getContractDraftById,
  getContractDraftsByProjectId,
  getInitials,
  getInquiriesByPackageId,
  getInquiriesByProjectId,
  getInquiryById,
  getInquiryDataForOffer,
  getKzmById,
  getKzmsByProjectId,
  getNextProjectId,
  getOfferById,
  getOffersByInquiryId,
  getOffersByProjectId,
  getOrderById,
  getOrdersByProjectId,
  getPackageById,
  getPackagesByProjectId,
  getProjectById,
  getProjectFinancialData,
  getProjectFolders,
  getScheduleItemsByProjectId,
  downloadScheduleCsvTemplate,
  importScheduleFromCsv,
  importProjectsFromDrive,
  sendDirectEmail,
  sendGmailDraft,
  updateContractDraft,
  updateFinancialData,
  updateInquiry,
  updateInquiryStatus,
  updateKzm,
  updateOffer,
  updateOrder,
  updateOrderStatus,
  updatePackage,
  updateProject,
  updateProjectPhase,
  updateScheduleItem
};
